import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  AppBar,
  Toolbar,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';
import useInvoice from '../../../../hooks/useInvoice';

/**
 * @param {Object} props
 * @param {boolean} props.open - Modal öffnen/schließen
 * @param {Function} props.onClose - Callback zum Schließen des Modals
 * @param {string} props.bookingId - ID der Buchung
 */
function InvoiceModal({ open, onClose, bookingId }) {
  const { getBookingInvoices, getInvoicePDF, isLoading } = useInvoice();
  const [invoices, setInvoices] = useState([]);
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [currentInvoice, setCurrentInvoice] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);

  useEffect(() => {
    if (open && bookingId) {
      loadInvoices();
    }
  }, [open, bookingId]);

  useEffect(() => {
    // Cleanup PDF URL when component unmounts or modal closes
    return () => {
      if (pdfUrl) {
        window.URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  const loadInvoices = async () => {
    const data = await getBookingInvoices(bookingId);
    setInvoices(data);
  };

  const handleDownload = async invoice => {
    const response = await getInvoicePDF(bookingId, invoice._id);
    if (response) {
      const url = window.URL.createObjectURL(new Blob([response], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Rechnung_${invoice.invoiceNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    }
  };

  const handleView = async invoice => {
    setCurrentInvoice(invoice);
    const response = await getInvoicePDF(bookingId, invoice._id);
    if (response) {
      // Cleanup previous URL if exists
      if (pdfUrl) {
        window.URL.revokeObjectURL(pdfUrl);
      }

      const url = window.URL.createObjectURL(new Blob([response], { type: 'application/pdf' }));
      setPdfUrl(url);
      setPdfViewerOpen(true);
    }
  };

  const handleClosePdfViewer = () => {
    setPdfViewerOpen(false);
    setCurrentInvoice(null);
  };

  const getStatusText = status => {
    const statusMap = {
      draft: 'Entwurf',
      sent: 'Gesendet',
      paid: 'Bezahlt',
      canceled: 'Storniert', // for internal bookings the american spelling is used
      cancelled: 'Storniert',
      fully_refunded: 'Vollständig erstattet',
      partially_refunded: 'Teilweise erstattet',
    };
    return statusMap[status] || status;
  };

  const getPaymentStatusText = status => {
    if (!status) return 'Zahlung ausstehend';
    const statusMap = {
      paid: 'Bezahlt',
      unpaid: 'Offen',
      initiated: 'Zahlung gestartet',
      pending: 'Zahlung wartet auf Bestätigung',
      authorised: 'Zahlung erfolgreich',
      captured: 'Zahlung erfolgreich',
      refunded: 'Zahlung vollständig zurückerstattet',
      partialRefund: 'Zahlung teilweise zurückerstattet',
      failed: 'Zahlung fehlgeschlagen',
      canceled: 'Zahlung abgebrochen', // for internal bookings the american spelling is used
      cancelled: 'Zahlung abgebrochen',
    };
    return statusMap[status] || status;
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
        <DialogTitle>Rechnungen</DialogTitle>
        <DialogContent>
          {isLoading ? (
            <Box display='flex' justifyContent='center' p={3}>
              <CircularProgress />
            </Box>
          ) : invoices.length === 0 ? (
            <Typography>Keine Rechnungen vorhanden</Typography>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Typ</TableCell>
                    <TableCell>Rechnungsnummer</TableCell>
                    <TableCell>Datum</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Zahlungsstatus</TableCell>
                    <TableCell>Betrag</TableCell>
                    <TableCell>Aktionen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoices.map(invoice => (
                    <TableRow key={invoice._id}>
                      <TableCell>
                        {invoice.type === 'original' ? 'Rechnung' : 'Stornorechnung'}
                      </TableCell>
                      <TableCell>{invoice.invoiceNumber}</TableCell>
                      <TableCell>{dayjs(invoice.meta.createdAt).format('DD.MM.YYYY')}</TableCell>
                      <TableCell>{getStatusText(invoice.status)}</TableCell>
                      <TableCell>{getPaymentStatusText(invoice.paymentStatus === 'paid' ? 'paid' : invoice.origin === 'externalPos' ? invoice.paymentStatus : invoice.relatedPaymentId?.status)}</TableCell>
                      <TableCell>{invoice.totalAmount.toFixed(2)} €</TableCell>
                      <TableCell>
                        <Tooltip title='Rechnung anzeigen'>
                          <IconButton size='small' onClick={() => handleView(invoice)}>
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title='Rechnung herunterladen'>
                          <IconButton size='small' onClick={() => handleDownload(invoice)}>
                            <DownloadIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Schließen</Button>
        </DialogActions>
      </Dialog>

      {/* PDF Viewer Modal */}
      <Dialog
        open={pdfViewerOpen}
        onClose={handleClosePdfViewer}
        maxWidth='lg'
        fullWidth
        fullScreen
      >
        <AppBar position='static' color='default' elevation={1}>
          <Toolbar>
            <Typography variant='h6' component='div' sx={{ flexGrow: 1 }}>
              {currentInvoice && `Rechnung ${currentInvoice.invoiceNumber}`}
            </Typography>
            <Tooltip title='Rechnung herunterladen'>
              <IconButton
                color='primary'
                onClick={() => currentInvoice && handleDownload(currentInvoice)}
                sx={{ mr: 2 }}
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            <IconButton edge='end' color='inherit' onClick={handleClosePdfViewer}>
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <DialogContent sx={{ p: 0, height: 'calc(100vh - 64px)' }}>
          {pdfUrl && (
            <iframe
              src={`${pdfUrl}#toolbar=0&navpanes=0`}
              style={{ width: '100%', height: '100%', border: 'none' }}
              title='PDF Viewer'
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default InvoiceModal;
